import { useState, useEffect, useMemo, useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { ref, onValue, off } from "firebase/database";
import { rtdb } from "@/config/firebase";
import { captureError } from "@/lib/errorReporting";
import { Icon, SkeletonCard } from "@/components/ui";
import Button from "@/components/ui/Button";

import heroImg from "@/assets/shapes/home-hero.png";


const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";

/* ─── Types ─── */
interface CabalItem {
    slug: string;
    name: string;
    ticker: string;
    imageUrl?: string;
    status: string;
    depositCurrent: number;
    depositTarget: number;
    memberCount: number;
    recruitingDeadline?: string;
    inviteCode: string;
    launchedAt?: string;
    tokenMint?: string;
}

interface TokenDetailsMap {
    [tokenMint: string]: {
        marketCap: string;
        priceChange24h: string;
    };
}

/* ════════════════════════════════════════════
   Home Page
   ════════════════════════════════════════════ */
export default function HomePage(): ReactNode {
    const navigate = useNavigate();
    const posthog = usePostHog();

    const [cabals, setCabals] = useState<CabalItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [tokenDetails, setTokenDetails] = useState<TokenDetailsMap>({});

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);

        fetch(`${API_BASE}/api/cabals`, { signal: controller.signal })
            .then((r) => r.json())
            .then((data) => setCabals(data))
            .catch((err) => {
                if (err.name !== "AbortError") captureError("Fetch cabals error:", err);
            })
            .finally(() => setLoading(false));

        return () => controller.abort();
    }, []);

    const fetchTokenDetails = useCallback(async (mints: string[]) => {
        const results: TokenDetailsMap = {};
        await Promise.allSettled(
            mints.map(async (mint) => {
                const res = await fetch(`${API_BASE}/api/coins/${mint}/details`);
                if (!res.ok) return;
                const data = await res.json();
                results[mint] = {
                    marketCap: data.marketCap || "$0",
                    priceChange24h: data.priceChange24h || "0%",
                };
            })
        );
        setTokenDetails(results);
    }, []);

    useEffect(() => {
        const launchedMints = cabals
            .filter((c) => c.status === "launched" && c.tokenMint)
            .sort((a, b) => new Date(b.launchedAt!).getTime() - new Date(a.launchedAt!).getTime())
            .slice(0, 5)
            .map((c) => c.tokenMint!);
        if (launchedMints.length > 0) fetchTokenDetails(launchedMints);
    }, [cabals, fetchTokenDetails]);

    /* ─── Real-time RTDB updates for recruiting cabals ─── */
    const [realtimeDeposits, setRealtimeDeposits] = useState<
        Record<string, { current: number; target: number; memberCount?: number }>
    >({});

    useEffect(() => {
        const recruitingSlugs = cabals
            .filter((c) => c.status === "recruiting")
            .map((c) => c.slug);

        if (recruitingSlugs.length === 0) return;

        const refs: ReturnType<typeof ref>[] = [];

        for (const slug of recruitingSlugs) {
            const depositRef = ref(rtdb, `cabals/${slug}/depositProgress`);
            const membersRef = ref(rtdb, `cabals/${slug}/members`);
            refs.push(depositRef, membersRef);

            onValue(depositRef, (snap) => {
                if (!snap.exists()) return;
                const val = snap.val();
                setRealtimeDeposits((prev) => ({
                    ...prev,
                    [slug]: { ...prev[slug], current: val.current, target: val.target },
                }));
            });

            onValue(membersRef, (snap) => {
                if (!snap.exists()) return;
                const val = snap.val();
                setRealtimeDeposits((prev) => ({
                    ...prev,
                    [slug]: { ...prev[slug], memberCount: val.count },
                }));
            });
        }

        return () => {
            for (const r of refs) off(r);
        };
    }, [cabals]);

    const activeCabals = useMemo(
        () => cabals
            .filter((c) => c.status === "recruiting")
            .map((c) => {
                const rt = realtimeDeposits[c.slug];
                if (!rt) return c;
                return {
                    ...c,
                    depositCurrent: rt.current ?? c.depositCurrent,
                    depositTarget: rt.target ?? c.depositTarget,
                    memberCount: rt.memberCount ?? c.memberCount,
                };
            })
            .sort((a, b) => b.depositCurrent - a.depositCurrent)
            .slice(0, 5),
        [cabals, realtimeDeposits]
    );

    const recentlyLaunched = useMemo(
        () => cabals
            .filter((c) => c.status === "launched" && c.launchedAt)
            .sort((a, b) => new Date(b.launchedAt!).getTime() - new Date(a.launchedAt!).getTime())
            .slice(0, 5),
        [cabals]
    );

    return (
        <div className="relative flex min-h-[100dvh] w-full flex-col overflow-hidden max-w-[430px] mx-auto bg-[#0a0a0a]">
            {/* Hero section — image extends from top, header overlaid */}
            <div className="relative w-full">
                <img
                    src={heroImg}
                    alt="Codev a Coin"
                    className="w-full object-cover max-h-[clamp(280px,45dvh,422px)]"
                    style={{ maskImage: "linear-gradient(to bottom, black 70%, transparent 100%)", WebkitMaskImage: "linear-gradient(to bottom, black 70%, transparent 100%)" }}
                />
                {/* Header overlaid on hero */}
                <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-[clamp(16px,2.6dvh,24px)] pt-[clamp(44px,6dvh,56px)] min-h-[clamp(122px,16.4dvh,153px)]">
                    <h1
                        className="text-white text-[clamp(34px,4.5dvh,42px)] font-bold leading-[1.15] tracking-[-0.84px]"
                        style={{ fontFamily: "var(--font-display)" }}
                    >
                        Home
                    </h1>
                    <Icon name="codev-logo" size={66} className="!w-[clamp(52px,7.1dvh,66px)] !h-[clamp(52px,7.1dvh,66px)]" />
                </div>
                {/* Codev a Coin CTA button */}
                <Button
                    variant="cta"
                    textStyle="v2"
                    onClick={() => {
                        posthog.capture("codev_it_clicked", { source: "home_hero_cta" });
                        navigate("/create");
                    }}
                    className="absolute bottom-[clamp(10px,1.7dvh,16px)] left-1/2 -translate-x-1/2 px-[clamp(18px,2.6dvh,24px)] py-[clamp(8px,1.1dvh,10px)] !rounded-[40.62px] !bg-[#f3ce28] !text-[#111012] text-[16px] z-10 border-[1.5px] border-white"
                >
                    Codev a Coin
                    <span className="text-[16px]">&rarr;</span>
                </Button>
            </div>

            <main className="relative z-10 flex-1 flex flex-col px-[20px] pb-[clamp(140px,18dvh,180px)]">

                {loading ? (
                    <div className="flex flex-col gap-[clamp(12px,1.7dvh,20px)] mt-[clamp(16px,2dvh,24px)]">
                        <SkeletonCard count={2} />
                    </div>
                ) : activeCabals.length > 0 && (
                    <>
                        <h2
                            className="text-white/50 text-[14px] font-semibold uppercase tracking-wide mb-[clamp(12px,1.5dvh,20px)] mt-[clamp(16px,2dvh,24px)]"
                            style={{ fontFamily: "var(--font-display)" }}
                        >
                            Live Launch Pools
                        </h2>
                        <div className="flex flex-col gap-[clamp(12px,1.7dvh,20px)]">
                            {activeCabals.map((cabal) => (
                                <LiveCodevCard
                                    key={cabal.slug}
                                    cabal={cabal}
                                    onClick={() => {
                                        posthog.capture("live_codev_clicked", {
                                            cabalSlug: cabal.slug,
                                            ticker: cabal.ticker,
                                            source: "homepage",
                                        });
                                        navigate(`/codevs/join/${cabal.inviteCode}`);
                                    }}
                                />
                            ))}
                        </div>
                    </>
                )}

                {!loading && recentlyLaunched.length > 0 && (
                    <>
                        <h2
                            className="text-white/50 text-[14px] font-semibold uppercase tracking-wide mb-[clamp(12px,1.5dvh,20px)] mt-[clamp(24px,3dvh,32px)]"
                            style={{ fontFamily: "var(--font-display)" }}
                        >
                            Recently Launched Coins
                        </h2>
                        <div className="flex flex-col gap-[clamp(12px,1.7dvh,20px)]">
                            {recentlyLaunched.map((cabal) => (
                                <LaunchedCoinCard
                                    key={cabal.slug}
                                    cabal={cabal}
                                    details={cabal.tokenMint ? tokenDetails[cabal.tokenMint] : undefined}
                                    onClick={() => {
                                        posthog.capture("launched_coin_clicked", {
                                            cabalSlug: cabal.slug,
                                            ticker: cabal.ticker,
                                            source: "homepage",
                                        });
                                        navigate(`/codevs/${cabal.slug}`);
                                    }}
                                />
                            ))}
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}

/* ════════════════════════
   Live Codev Card
   ════════════════════════ */
function LiveCodevCard({
    cabal,
    onClick,
}: {
    cabal: CabalItem;
    onClick: () => void;
}): ReactNode {
    const percent = cabal.depositTarget > 0
        ? Math.min(100, Math.round((cabal.depositCurrent / cabal.depositTarget) * 100))
        : 0;

    const timeLeft = useTimeLeft(cabal.recruitingDeadline);

    return (
        <button
            onClick={onClick}
            className="w-full text-left rounded-[12px] p-[clamp(12px,1.7dvh,16px)] cursor-pointer active:scale-[0.98] transition-transform"
            style={{
                background: "linear-gradient(to bottom, #171717, #131313)",
                border: "1px solid rgba(153,153,153,0.3)",
            }}
        >
            {/* Header: avatar + ticker + LIVE badge */}
            <div className="flex items-center gap-[clamp(8px,1.3dvh,12px)] mb-[clamp(8px,1.3dvh,12px)]">
                <div className="size-[clamp(32px,4.3dvh,40px)] rounded-full overflow-hidden bg-white/10 shrink-0 flex items-center justify-center">
                    {cabal.imageUrl ? (
                        <img src={cabal.imageUrl} alt={cabal.ticker} className="size-full object-contain" />
                    ) : (
                        <Icon name="token" size={20} />
                    )}
                </div>
                <span
                    className="text-white text-[16px] font-bold flex-1 truncate"
                    style={{ fontFamily: "var(--font-display)" }}
                >
                    {cabal.ticker.toUpperCase()}
                </span>
                <span
                    className="text-white/60 text-[12px] font-semibold uppercase tracking-wider px-[12px] py-[4px] rounded-full"
                    style={{
                        border: "1px solid rgba(255,255,255,0.2)",
                        fontFamily: "var(--font-display)",
                    }}
                >
                    Live
                </span>
            </div>

            {/* Progress row */}
            <div className="flex items-center justify-between mb-[6px]">
                <span
                    className="text-[14px] font-bold"
                    style={{ fontFamily: "var(--font-display)" }}
                >
                    {parseFloat(cabal.depositCurrent.toFixed(2))} / {parseFloat(cabal.depositTarget.toFixed(2))} SOL
                </span>
                <span
                    className="text-[14px] font-bold"
                    style={{ color: "#f3ce28", fontFamily: "var(--font-display)" }}
                >
                    {percent}%
                </span>
            </div>

            {/* Progress bar */}
            <div className="h-[6px] w-full bg-white/10 rounded-full overflow-hidden mb-[clamp(10px,1.7dvh,16px)]">
                <div
                    className="h-full rounded-full"
                    style={{ width: `${percent}%`, backgroundColor: "#f3ce28" }}
                />
            </div>

            {/* Stats: Members + Time Left */}
            <div className="flex">
                <div className="flex-1">
                    <p
                        className="text-white/40 text-[12px] font-semibold mb-[2px]"
                        style={{ fontFamily: "var(--font-display)" }}
                    >
                        Members
                    </p>
                    <p
                        className="text-white text-[16px] font-bold"
                        style={{ fontFamily: "var(--font-display)" }}
                    >
                        {cabal.memberCount}
                    </p>
                </div>
                <div className="flex-1">
                    <p
                        className="text-white/40 text-[12px] font-semibold mb-[2px]"
                        style={{ fontFamily: "var(--font-display)" }}
                    >
                        Time Left
                    </p>
                    <p
                        className="text-white text-[16px] font-bold"
                        style={{ fontFamily: "var(--font-display)" }}
                    >
                        {timeLeft}
                    </p>
                </div>
            </div>
        </button>
    );
}

/* ════════════════════════
   Launched Coin Card
   ════════════════════════ */
function LaunchedCoinCard({
    cabal,
    details,
    onClick,
}: {
    cabal: CabalItem;
    details?: { marketCap: string; priceChange24h: string };
    onClick: () => void;
}): ReactNode {
    const changeStr = details?.priceChange24h || "0%";
    const changeNum = parseFloat(changeStr.replace("%", "")) || 0;
    const isPositive = changeNum >= 0;

    return (
        <button
            onClick={onClick}
            className="w-full text-left rounded-[12px] p-[clamp(12px,1.7dvh,16px)] cursor-pointer active:scale-[0.98] transition-transform"
            style={{
                background: "linear-gradient(to bottom, #171717, #131313)",
                border: "1px solid rgba(153,153,153,0.3)",
            }}
        >
            {/* Header: avatar + ticker + time ago badge */}
            <div className="flex items-center gap-[clamp(8px,1.3dvh,12px)] mb-[clamp(8px,1.3dvh,12px)]">
                <div className="size-[clamp(32px,4.3dvh,40px)] rounded-full overflow-hidden bg-white/10 shrink-0 flex items-center justify-center">
                    {cabal.imageUrl ? (
                        <img src={cabal.imageUrl} alt={cabal.ticker} className="size-full object-contain" />
                    ) : (
                        <Icon name="token" size={20} />
                    )}
                </div>
                <span
                    className="text-white text-[16px] font-bold flex-1 truncate"
                    style={{ fontFamily: "var(--font-display)" }}
                >
                    {cabal.ticker.toUpperCase()}
                </span>
                <span
                    className="text-white/60 text-[12px] font-semibold px-[12px] py-[4px] rounded-full"
                    style={{
                        border: "1px solid rgba(255,255,255,0.2)",
                        fontFamily: "var(--font-display)",
                    }}
                >
                    {timeAgo(cabal.launchedAt)}
                </span>
            </div>

            {/* Stats: MCAP + 24H */}
            <div className="flex">
                <div className="flex-1">
                    <p
                        className="text-white/40 text-[12px] font-semibold mb-[2px]"
                        style={{ fontFamily: "var(--font-display)" }}
                    >
                        MCAP
                    </p>
                    <p
                        className="text-white text-[16px] font-bold"
                        style={{ fontFamily: "var(--font-display)" }}
                    >
                        {details?.marketCap || "—"}
                    </p>
                </div>
                <div className="flex-1">
                    <p
                        className="text-white/40 text-[12px] font-semibold mb-[2px]"
                        style={{ fontFamily: "var(--font-display)" }}
                    >
                        24H Change
                    </p>
                    <p
                        className="text-[16px] font-bold"
                        style={{
                            color: isPositive ? "#22c55e" : "#ef4444",
                            fontFamily: "var(--font-display)",
                        }}
                    >
                        {details ? `${isPositive ? "+" : ""}${changeStr}` : "—"}
                    </p>
                </div>
            </div>
        </button>
    );
}


/* ════════════════════════
   Time Ago Helper
   ════════════════════════ */
function timeAgo(dateStr?: string): string {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    if (diff < 0) return "just now";

    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;

    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    if (days === 1) return "1 day ago";
    return `${days} days ago`;
}

/* ════════════════════════
   Time Left Hook
   ════════════════════════ */
function useTimeLeft(deadline?: string): string {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!deadline) return;
        const interval = setInterval(() => setNow(Date.now()), 30_000);
        return () => clearInterval(interval);
    }, [deadline]);

    if (!deadline) return "--";

    const diff = new Date(deadline).getTime() - now;
    if (diff <= 0) return "Ended";

    const hours = Math.floor(diff / 3_600_000);
    const mins = Math.floor((diff % 3_600_000) / 60_000);

    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
}
